import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppTFunction } from '@retail/app/i18n';
import {
  EditFreightConditionSchema,
  editFreightConditionSchema,
  FixedAddonFreightForm,
  FreightConditionPercentAddonInput,
} from '@retail/freight/components';
import { ConditionsNew } from '@retail/calculus/types';
import dayjs from 'dayjs';
import { useFetchGlobalItemUnits } from '@retail/freight/data-access';
import { useConditionActions, useFetchSelectedLevelOptions } from '@retail/calculus/context';
import { BodyLoader, withDialogSuspenseErrorBoundary } from '@retail/components';
import { Unit } from '@retail/products/types';
import { EditConditionDialog } from '../ConditionActionsBase';

interface Props {
  open: boolean;
  supplierId: number;
  freightConditionToEdit: ConditionsNew;
  onClose: () => void;
}

function EditFreightConditionDialog({
  open,
  supplierId,
  freightConditionToEdit,
  onClose,
}: Props) {
  const t = useAppTFunction();
  const { createOrUpdateCondition } = useConditionActions();
  const { mgItem } = useFetchSelectedLevelOptions({
    supplierNumber: supplierId,
    selectedMgItemNumber: freightConditionToEdit.bindingType === 'MG_SUPPLIER_ITEM' ? Number(freightConditionToEdit.bindingTargetId) : undefined,
  });
  const supplierItemPackages = useMemo(
    () => mgItem?.getSupplierItem(supplierId)?.mgSupplierItemPackages ?? [],
    [mgItem, supplierId]
  );
  const { data: globalItemUnitsDTO = [], isLoading } = useFetchGlobalItemUnits();
  const globalItemUnits = globalItemUnitsDTO.map(Unit.fromDto);

  const unitsForFixedAddon = useMemo(() => {
    const supplierItemUnits = supplierItemPackages
      ? supplierItemPackages.map((pkge) => {
        return { ...pkge.unit };
      })
      : [];
    return [...supplierItemUnits, ...globalItemUnits];
  }, [globalItemUnits, supplierItemPackages]);

  const formMethods = useForm<EditFreightConditionSchema>({
    defaultValues: {
      percentAddon: freightConditionToEdit.freightCondition?.percentAddon,
      fixedAddon: freightConditionToEdit.freightCondition?.fixedAddOn,
      fixedAddonUnit: freightConditionToEdit.freightCondition?.fixedAddOnUnit,
      freightType: freightConditionToEdit.freightCondition?.percentAddon
        ? 'percent'
        : 'fixed_addon',
      fromDate: new Date(),
      toDate: freightConditionToEdit.freightCondition?.validTo
        ? new Date(freightConditionToEdit.freightCondition?.validTo)
        : undefined,
    },
    resolver: yupResolver(editFreightConditionSchema(t)),
  });

  const onEdit = useCallback(
    (data: EditFreightConditionSchema) => {
      createOrUpdateCondition({
        content: {
          freight: data.percentAddon ? { percentAddon: data.percentAddon } : undefined,
          freightFixed:
            data.fixedAddon && data.fixedAddonUnit
              ? { fixedAddOn: data.fixedAddon, fixedAddOnUnit: data.fixedAddonUnit }
              : undefined,
        },
        bindingValue: freightConditionToEdit.getBindingLevel(),
        validFrom: dayjs(data.fromDate).isAfter(dayjs()) ? data.fromDate : new Date(),
      }).then(onClose);
    },
    [createOrUpdateCondition, freightConditionToEdit, onClose]
  );

  return (
    <EditConditionDialog
      open={open}
      title={t('freight.form.editFreightConditionFor', { condition: freightConditionToEdit.bindingTargetName })}
      dialogContent={
        <FormProvider {...formMethods}>
          {formMethods.getValues('freightType') === 'fixed_addon' ? (
            !isLoading ? (
              <FixedAddonFreightForm unitsForFixedAddon={unitsForFixedAddon} />
            ) : (
              <BodyLoader />
            )
          ) : (
            <FreightConditionPercentAddonInput />
          )}
        </FormProvider>
      }
      isDisabled={Object.keys(formMethods?.formState?.errors).length > 0}
      onClose={onClose}
      onSubmit={formMethods.handleSubmit(onEdit)}
    />

  );
}

export default withDialogSuspenseErrorBoundary(EditFreightConditionDialog);
